import { useEffect, useRef, useState, useMemo } from "react";
import {
  CLIENT_ADD_DEFAULT_PLATFORMS,
  getPlansByType,
  MONITORING_TO_SUBSCRIPTION_TYPE,
  type SubscriptionPlan,
} from "@/constants/plans";
import type { MonitoringType } from "@/constants/monitoring";
import type { ClientAddCardData } from "../components/ClientAdd/MonitoringTabContent";
import type { SubscriptionDetails } from "../types/client.types";
import { getInitialPlansStateFromSubscriptions } from "@/features/admin/utils/subscriptionToMonitoring";

export interface SelectedPlan {
  plan_id: string;
  monitoringType: MonitoringType;
  planTitle: string;
  price: string;
  platforms: string[];
  included?: string[];
}

export function planToCard(
  plan: SubscriptionPlan,
  isFirstTier: boolean,
): ClientAddCardData {
  const title = plan.tier.charAt(0).toUpperCase() + plan.tier.slice(1);
  const TIER1_PLAN_ID = "69730f65385ceb78ddd72b87";
  return {
    plan_id: plan._id,
    title,
    price:
      plan._id === TIER1_PLAN_ID ? "$100 - Tier 1" : `$${plan.base_price}/mo`,
    included: [
      `Rate limit: ${plan.calls_per_min} calls/min`,
      `Monitoring ${plan.max_platforms} platform(s)`,
      ...(plan.default_quota
        ? [`Total quota: ${plan.default_quota.toLocaleString()}`]
        : []),
      ...(plan.influencer_channels != null
        ? [`Up to ${plan.influencer_channels} influencer channels`]
        : []),
      ...(plan.pulse_updates != null
        ? [`Pulse updates: ${plan.pulse_updates.toLocaleString()}`]
        : []),
    ].filter(Boolean),
    popular: isFirstTier,
  };
}

const DEFAULT_TAB_DISABLED = {
  keyword: true,
  influencer: true,
  source: true,
  pulse: true,
} as const;

export function useClientPlans(
  initialSubscriptions?: SubscriptionDetails[] | null,
) {
  const [selectedPlans, setSelectedPlans] = useState<Map<string, SelectedPlan>>(
    new Map(),
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [tabDisabled, setTabDisabled] = useState<
    Record<MonitoringType, boolean>
  >({ ...DEFAULT_TAB_DISABLED });
  const hasInitializedFromSubscriptions = useRef(false);

  useEffect(() => {
    if (
      hasInitializedFromSubscriptions.current ||
      !initialSubscriptions?.length
    ) {
      return;
    }
    hasInitializedFromSubscriptions.current = true;
    const {
      selectedPlans: initial,
      tabDisabled: disabled,
      expandedIds: ids,
    } = getInitialPlansStateFromSubscriptions(initialSubscriptions);
    setSelectedPlans(initial as Map<string, SelectedPlan>);
    setTabDisabled(disabled);
    setExpandedIds(ids);
  }, [initialSubscriptions]);

  // Cards derived from plans config (show all plans; edit mode disables switching in UI)
  const keywordCards = useMemo(() => {
    return getPlansByType(MONITORING_TO_SUBSCRIPTION_TYPE.keyword).map((p, i) =>
      planToCard(p, i === 0),
    );
  }, []);

  const influencerCards = useMemo(() => {
    return getPlansByType(MONITORING_TO_SUBSCRIPTION_TYPE.influencer).map(
      (p, i) => planToCard(p, i === 0),
    );
  }, []);

  const sourceCards = useMemo(() => {
    return getPlansByType(MONITORING_TO_SUBSCRIPTION_TYPE.source).map((p, i) =>
      planToCard(p, i === 0),
    );
  }, []);

  const pulseCards = useMemo(() => {
    return getPlansByType(MONITORING_TO_SUBSCRIPTION_TYPE.pulse).map((p, i) =>
      planToCard(p, i === 0),
    );
  }, []);

  const handlePlatformChange = (
    monitoringType: MonitoringType,
    planId: string,
    planTitle: string,
    platforms: string[],
    price: string,
    included?: string[],
  ) => {
    const key = `${monitoringType}-${planTitle}`;

    setSelectedPlans((prev) => {
      const next = new Map(prev);

      if (platforms.length === 0) {
        next.delete(key);
        return next;
      }

      const isNew = !next.has(key);

      // Clear any other plan of the same monitoring type
      for (const existingKey of next.keys()) {
        if (
          existingKey.startsWith(`${monitoringType}-`) &&
          existingKey !== key
        ) {
          next.delete(existingKey);
          setExpandedIds((ids) => {
            const s = new Set(ids);
            s.delete(existingKey);
            return s;
          });
        }
      }

      next.set(key, {
        plan_id: planId,
        monitoringType,
        planTitle,
        price,
        platforms,
        included,
      });

      if (isNew) {
        setExpandedIds((ids) => new Set([...ids, key]));
      }

      return next;
    });
  };

  const handleDisabledChange = (
    monitoringType: MonitoringType,
    disabled: boolean,
  ) => {
    setTabDisabled((prev) => ({ ...prev, [monitoringType]: disabled }));

    if (disabled) {
      setSelectedPlans((prev) => {
        const next = new Map(prev);
        for (const key of next.keys()) {
          if (key.startsWith(`${monitoringType}-`)) next.delete(key);
        }
        return next;
      });

      setExpandedIds((prev) => {
        const next = new Set(prev);
        for (const id of next) {
          if (id.startsWith(`${monitoringType}-`)) next.delete(id);
        }
        return next;
      });
    } else if (monitoringType === "source") {
      // Source Monitoring: when the switch is turned on, subscribe to the Usage Based plan (only source plan)
      const usageBasedSourceCard = sourceCards[0];
      if (usageBasedSourceCard) {
        const key = `source-${usageBasedSourceCard.title}`;
        setSelectedPlans((prev) => {
          const next = new Map(prev);
          next.set(key, {
            plan_id: usageBasedSourceCard.plan_id,
            monitoringType: "source",
            planTitle: usageBasedSourceCard.title,
            price: usageBasedSourceCard.price,
            platforms: [...CLIENT_ADD_DEFAULT_PLATFORMS],
            included: usageBasedSourceCard.included,
          });
          return next;
        });
        setExpandedIds((prev) => new Set([...prev, key]));
      }
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const totalPrice = useMemo(() => {
    let total = 0;
    selectedPlans.forEach((plan) => {
      const num = parseFloat(plan.price.replace(/[$,/mo]/g, ""));
      if (!isNaN(num)) total += num;
    });
    return total;
  }, [selectedPlans]);

  const subscriptions = useMemo(() => {
    return Array.from(selectedPlans.entries()).map(([key, plan]) => ({
      id: key,
      name: `${plan.monitoringType.charAt(0).toUpperCase() + plan.monitoringType.slice(1)} Monitoring`,
      price: plan.price,
      plan: plan.planTitle,
      platforms: plan.platforms,
      included: plan.included ?? [],
    }));
  }, [selectedPlans]);

  return {
    // State
    selectedPlans,
    expandedIds,
    tabDisabled,
    // Derived
    totalPrice,
    subscriptions,
    hasSelections: selectedPlans.size > 0,
    // Cards
    keywordCards,
    influencerCards,
    sourceCards,
    pulseCards,
    // Handlers
    handlePlatformChange,
    handleDisabledChange,
    toggleExpanded,
  };
}
